import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

const buttonVariants = tv({
  base: "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  variants: {
    variant: {
      default:     'bg-primary text-primary-foreground hover:bg-primary-hover',
      destructive: 'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
      outline:     'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
      secondary:   'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      ghost:       'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
      link:        'text-primary underline-offset-4 hover:underline',
      success:     'bg-success text-success-foreground hover:bg-success/90',
      warning:     'bg-warning text-warning-foreground hover:bg-warning/90',
    },
    size: {
      default:   'h-9 px-4 py-2 has-[>svg]:px-3',
      sm:        'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
      lg:        'h-10 rounded-md px-6 has-[>svg]:px-4',
      // Full-width CTA row buttons (contact sheet, modal footers)
      action:    'rounded-lg px-4 py-3 text-sm font-semibold has-[>svg]:px-3',
      icon:      'size-9',
      'icon-sm': 'size-8',
      'icon-lg': 'size-10',
      // Filter chips / pill toggles
      pill:      'rounded-full px-3.5 py-1.5 text-sm font-medium has-[>svg]:px-3',
      // Floating action buttons
      fab:       'size-14 rounded-full shadow-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={buttonVariants({ variant, size, class: className })}
      {...props}
    />
  );
}

export { Button, buttonVariants };
